import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useParams } from 'react-router'

import { Icon } from '@iconify/react'
import { Col, Row } from 'antd'
import { useFormik } from 'formik'

import { useNotification } from 'app/providers'
import { useAuth } from 'app/providers/auth/AuthProvider'

import { Button } from 'common/components/Button/Button'
import { ErrorLabel } from 'common/components/ErrorLabel/ErrorLabel'
import { Input } from 'common/components/Input/Input'
import { InputNumber } from 'common/components/InputNumber/InputNumber'
import { Label } from 'common/components/Label/Label'
import Popup from 'common/components/Popup'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { useAppSelector } from 'common/hooks/redux'

import { CodeSelect } from 'features/Procedure/features/treatments/components/code-select/code-select'
import {
  DiscountHelperText,
  DiscountSelect,
} from 'features/Procedure/features/treatments/components/discount-select/discount-select'
import {
  scannerLog,
  scannerWarn,
} from 'features/Procedure/features/treatments/components/barcode-scanner/scanner-log'
import {
  computeDiscountAmount,
  findDiscountTypeById,
  getDefaultDiscountTypeIdForContext,
  isDiscountVisibleForForm,
} from 'features/Procedure/features/treatments/components/discount-select/discount-utils'
import { hasProcedureEnteredData } from 'features/Procedure/features/treatments/utils'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import type {
  EmrCodeItem,
  ProcedureDto,
  ProcedureItem,
} from 'features/Procedure/state/procedure-api.type'
import {
  useCreateProductMutation,
  useDeleteProcedureMutation,
  useFetchProductsQuery,
  useLazyFetchEmrScanProductQuery,
  useUpdateProductMutation,
} from 'features/Procedure/state/procedure.api'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'

import styles from './manage-product.module.scss'
import { validationSchema, type FormValues } from './schema-product'
import { UpcSelect } from './upc-select'
import { formatJustifyCodes, formatProductLabelsWithUpc, getInitialValues } from './utils-product'

export type ManageProductFormHandle = {
  scanUpc: (upc: string) => Promise<void>
  tryClose: () => void
}

type ManageProductFormProps = {
  procedure?: ProcedureItem | null
  forceAddMode?: boolean
  selectedEncounterUid: string | null
  onClose: () => void
  scannerSlot?: ReactNode
}

export const ManageProductForm = forwardRef<ManageProductFormHandle, ManageProductFormProps>(
  ({ procedure, forceAddMode = false, selectedEncounterUid, onClose, scannerSlot }, ref) => {
    const { siteId } = useParams<{ siteId: string }>()

    const { emrId } = useAuth()

    const { setNotification } = useNotification()
    const { handleSetSelectedProcedure } = useProcedureActions()
    const drawingElements = useAppSelector((state) => state.procedureReducer.drawingElements)

    const { data: productsData } = useFetchProductsQuery(
      { siteId: siteId ?? '', encounterUid: selectedEncounterUid ?? '' },
      { skip: !siteId || !selectedEncounterUid },
    )

    const discountTypes = productsData?.discount_type
    const patientDefaultDiscount = productsData?.patient_default_discount ?? null
    const isDiscountVisible = isDiscountVisibleForForm(patientDefaultDiscount, 'products')

    const [createProduct] = useCreateProductMutation()
    const [updateProduct] = useUpdateProductMutation()
    const [deleteProcedure, { isLoading: isDeleting }] = useDeleteProcedureMutation()
    const [triggerScanProduct] = useLazyFetchEmrScanProductQuery()
    const [isScanning, setIsScanning] = useState(false)
    const scanInFlightRef = useRef(false)
    const [scanError, setScanError] = useState<string | null>(null)
    const scanErrorTimeoutRef = useRef<number | null>(null)

    const showScanError = useCallback((message: string) => {
      setScanError(message)
      if (scanErrorTimeoutRef.current) {
        window.clearTimeout(scanErrorTimeoutRef.current)
      }
      scanErrorTimeoutRef.current = window.setTimeout(() => {
        setScanError(null)
        scanErrorTimeoutRef.current = null
      }, 5000)
    }, [])

    const dismissScanError = useCallback(() => {
      if (scanErrorTimeoutRef.current) {
        window.clearTimeout(scanErrorTimeoutRef.current)
        scanErrorTimeoutRef.current = null
      }
      setScanError(null)
    }, [])

    useEffect(() => {
      return () => {
        if (scanErrorTimeoutRef.current) {
          window.clearTimeout(scanErrorTimeoutRef.current)
        }
      }
    }, [])

    const isEditMode = !!procedure && !forceAddMode
    const isBilledProduct = !forceAddMode && Number(procedure?.billed) === 1
    const entityName = 'Product'

    const defaultDiscountTypeId = useMemo(
      () => getDefaultDiscountTypeIdForContext(patientDefaultDiscount, 'products'),
      [patientDefaultDiscount],
    )

    const initialValues: FormValues = useMemo(() => {
      return getInitialValues(procedure, defaultDiscountTypeId)
    }, [procedure, defaultDiscountTypeId])

    const procedureHasEnteredData = useMemo(
      () => hasProcedureEnteredData(procedure, drawingElements),
      [drawingElements, procedure],
    )

    const handleSubmitForm = useCallback(
      async (values: FormValues, { resetForm }: { resetForm: () => void }) => {
        try {
          if (!siteId || !selectedEncounterUid) {
            setNotification({
              type: ALERT_CONSTANTS.ERROR,
              duration: 2,
              title: 'Error',
              description: 'Missing site or encounter information',
            })
            return
          }

          const justifyCodes = formatJustifyCodes((values.icd10_codes || []) as EmrCodeItem[])

          const selectedDiscount = findDiscountTypeById(discountTypes, values.discount_type_id)

          const procedureDto: ProcedureDto = {
            ...(isEditMode && procedure?.id ? { id: procedure.id } : {}),
            code: values.cpt4_code.code,
            code_type: ProcedureTypeCode.CPT4,
            procedure_units: values.procedure_units ?? undefined,
            units: values.units,
            fee: values.price,
            provider_id: emrId,
            justify_codes: justifyCodes,
            product_labels: formatProductLabelsWithUpc(procedure?.product_labels, values.upc),
            ...(selectedDiscount && {
              discount_type: selectedDiscount.option_id,
              discount_name: selectedDiscount.title,
              discount_amount: computeDiscountAmount(values.price, selectedDiscount),
            }),
          }

          const mutationFn = isEditMode ? updateProduct : createProduct
          const result = await mutationFn({
            siteId,
            encounterUid: selectedEncounterUid,
            data: {
              procedures: [procedureDto],
            },
          }).unwrap()

          if (result && typeof result === 'object' && 'success' in result) {
            const responseResult = result as {
              success: boolean
              error?: string
              statusCode?: number
            }
            if (!responseResult.success) {
              setNotification({
                type: ALERT_CONSTANTS.ERROR,
                duration: 2,
                title: 'Error',
                description: responseResult.error || 'Failed to process product',
              })
              return
            }
          }

          if (!isEditMode && result && typeof result === 'object' && 'added_procedures' in result) {
            const mutationResult = result as { added_procedures?: ProcedureItem[] }
            if (mutationResult.added_procedures && mutationResult.added_procedures.length > 0) {
              handleSetSelectedProcedure(mutationResult.added_procedures[0])
            }
          }

          resetForm()
          onClose()
        } catch (error: any) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description:
              error?.data?.message ||
              `Failed to ${isEditMode ? 'update' : 'create'} ${entityName.toLowerCase()}`,
          })
        }
      },
      [
        siteId,
        selectedEncounterUid,
        emrId,
        isEditMode,
        updateProduct,
        createProduct,
        setNotification,
        onClose,
        entityName,
        procedure,
        handleSetSelectedProcedure,
        discountTypes,
      ],
    )

    const { values, errors, isSubmitting, handleSubmit, setFieldValue, resetForm, handleChange } =
      useFormik({
        initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        onSubmit: handleSubmitForm,
      })
    const currentUpcRef = useRef(values.upc ?? '')

    useEffect(() => {
      currentUpcRef.current = values.upc ?? ''
    }, [values.upc])

    const getErrorMessage = (error: any): string | undefined => {
      if (!error) return undefined
      if (typeof error === 'string') return error
      if (error?.message) return error.message
      return undefined
    }

    const handleDelete = useCallback(async () => {
      try {
        if (!siteId || !selectedEncounterUid || !procedure?.id) {
          setNotification({
            type: ALERT_CONSTANTS.ERROR,
            duration: 2,
            title: 'Error',
            description: 'Missing site, encounter, or product information',
          })
          return
        }

        const result = await deleteProcedure({
          siteId,
          encounterUid: selectedEncounterUid,
          procedureId: procedure.id,
        }).unwrap()

        if (result && typeof result === 'object' && 'success' in result) {
          const responseResult = result as { success: boolean; error?: string; statusCode?: number }
          if (!responseResult.success) {
            setNotification({
              type: ALERT_CONSTANTS.ERROR,
              duration: 2,
              title: 'Error',
              description: responseResult.error || 'Failed to delete product',
            })
            return
          }
        }

        onClose()
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: error?.data?.message || 'Failed to delete product',
        })
      }
    }, [siteId, selectedEncounterUid, procedure?.id, deleteProcedure, setNotification, onClose])

    const handleDeleteClick = useCallback(() => {
      if (!procedureHasEnteredData) {
        void handleDelete()
        return
      }

      Popup.showInfo(
        {
          title: 'Warning',
          message: 'Procedure has data which will be deleted. Proceed?',
          mainButtonText: 'Proceed',
          secondButtonText: 'Cancel',
        },
        () => {
          void handleDelete()
        },
      )
    }, [handleDelete, procedureHasEnteredData])

    const handleProductChange = useCallback(
      (newValue: EmrCodeItem[] | EmrCodeItem | null) => {
        setFieldValue('cpt4_code', newValue)

        if (newValue && !Array.isArray(newValue)) {
          if (typeof newValue.default_price === 'number' && newValue.default_price >= 0) {
            setFieldValue('price', newValue.default_price)
          }

          const nextUpc = (newValue.upc ?? '').trim()
          if (nextUpc) {
            currentUpcRef.current = nextUpc
            setFieldValue('upc', nextUpc)
          }
        }
      },
      [setFieldValue],
    )

    const handleUpcProductSelect = useCallback(
      (item: EmrCodeItem) => {
        setFieldValue('cpt4_code', item)

        if (typeof item.default_price === 'number' && item.default_price >= 0) {
          setFieldValue('price', item.default_price)
        }
      },
      [setFieldValue],
    )

    const handleUpcChange = useCallback(
      (upc: string) => {
        currentUpcRef.current = upc
        setFieldValue('upc', upc)
      },
      [setFieldValue],
    )

    const handleUpcScan = useCallback(
      async (upc: string) => {
        scannerLog('handleUpcScan called with', upc)

        if (scanInFlightRef.current) {
          scannerLog('handleUpcScan: bailing because scanInFlightRef is true')
          return
        }

        const normalizedUpc = upc.trim()

        if (!normalizedUpc) {
          scannerLog('handleUpcScan: bailing because normalizedUpc is empty')
          return
        }

        if (currentUpcRef.current.trim() === normalizedUpc) {
          scannerLog('handleUpcScan: bailing because currentUpcRef already equals', normalizedUpc)
          return
        }

        if (!siteId) {
          scannerWarn('handleUpcScan: missing siteId, cannot call BE')
          showScanError('Missing site information')
          return
        }

        scanInFlightRef.current = true
        setIsScanning(true)
        scannerLog('handleUpcScan: calling BE triggerScanProduct with', {
          siteId,
          search: normalizedUpc,
        })
        try {
          const response = await triggerScanProduct(
            { siteId, search: normalizedUpc },
            false,
          ).unwrap()

          scannerLog('handleUpcScan: BE response =', response)

          if (!response.ok) {
            const errorResponse = response as { hint?: string; error?: string }
            scannerWarn(
              'handleUpcScan: BE returned ok=false; hint =',
              errorResponse.hint,
              'error =',
              errorResponse.error,
            )

            setFieldValue('upc', normalizedUpc)
            currentUpcRef.current = normalizedUpc

            const isNotFound = errorResponse.error === 'not_found'
            const description = isNotFound
              ? `Product not found: UPC ${normalizedUpc} is not in the product catalog. Pick a product manually or add it to the catalog.`
              : errorResponse.hint ||
                errorResponse.error ||
                `Could not resolve UPC ${normalizedUpc}`

            showScanError(description)
            return
          }

          const resolvedUpc = (response.upc ?? normalizedUpc).trim()
          const resolvedItem: EmrCodeItem = {
            id: response.id,
            code: response.code,
            code_text: response.code_text ?? '',
            code_text_short: response.code_text_short ?? '',
            code_type: ProcedureTypeCode.CPT4,
            active: (response.active ?? 1) as EmrCodeItem['active'],
            default_price: response.default_price ?? undefined,
            upc: resolvedUpc || undefined,
          }

          scannerLog(
            'handleUpcScan: BE matched; populating form with upc =',
            resolvedUpc,
            ' code =',
            response.code,
            ' price =',
            response.default_price,
          )
          currentUpcRef.current = resolvedUpc
          setFieldValue('upc', resolvedUpc)
          setFieldValue('cpt4_code', resolvedItem)
          if (typeof response.default_price === 'number' && response.default_price >= 0) {
            setFieldValue('price', response.default_price)
          }
          dismissScanError()
        } catch (error: any) {
          scannerWarn('handleUpcScan: BE threw', error?.status, error?.data, error?.message)
          showScanError(
            error?.data?.hint ||
              error?.data?.error ||
              error?.data?.message ||
              'Could not resolve scanned product',
          )
        } finally {
          scanInFlightRef.current = false
          setIsScanning(false)
        }
      },
      [siteId, triggerScanProduct, setFieldValue, showScanError, dismissScanError],
    )

    const handleAutoScan = useCallback(
      (upc: string) => {
        currentUpcRef.current = ''
        void handleUpcScan(upc)
      },
      [handleUpcScan],
    )

    useImperativeHandle(
      ref,
      () => ({
        scanUpc: handleUpcScan,
        tryClose: () => {
          if (isDeleting || isScanning) return
          resetForm()
          onClose()
        },
      }),
      [handleUpcScan, isDeleting, isScanning, resetForm, onClose],
    )

    return (
      <form
        onSubmit={handleSubmit}
        className={styles.form}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault()
        }}>
        <SpinnerOverlay isLoading={isSubmitting || isDeleting || isScanning} />
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12}>
            <CodeSelect
              required
              label={entityName}
              codeType={ProcedureTypeCode.CPT4}
              size='large'
              value={values.cpt4_code as EmrCodeItem | null}
              onChange={handleProductChange}
              placeholder='Search CPT code'
              error={getErrorMessage(errors.cpt4_code)}
              category='products'
              disabled={isBilledProduct}
            />
          </Col>
          <Col xs={24} sm={12}>
            <UpcSelect
              label='UPC'
              value={values.upc ?? ''}
              onChange={handleUpcChange}
              onSelectProduct={handleUpcProductSelect}
              onScan={handleAutoScan}
              placeholder='UPC'
              disabled={isBilledProduct}
              error={errors.upc as string | undefined}
              autoFocus={!isEditMode}
            />
          </Col>
          <Col xs={24}>
            <CodeSelect
              label='ICD'
              codeType={ProcedureTypeCode.ICD10}
              mode='multiple'
              size='large'
              value={values.icd10_codes as EmrCodeItem[]}
              onChange={(newValue) => setFieldValue('icd10_codes', newValue)}
              placeholder='Search ICD codes'
              disabled={isBilledProduct}
              error={getErrorMessage(errors.icd10_codes)}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Label label='Quantity' />
            <InputNumber
              value={values.units}
              name='units'
              onChange={(stringValue) => setFieldValue('units', stringValue)}
              placeholder='Quantity'
              min={1}
              disabled={isBilledProduct}
              status={errors.units ? 'error' : ''}
            />
            <ErrorLabel error={errors.units} />
          </Col>
          {isDiscountVisible && (
            <Col xs={24} sm={8}>
              <DiscountSelect
                value={values.discount_type_id ?? null}
                onChange={(optionId) => setFieldValue('discount_type_id', optionId)}
                formContext='products'
                discountTypes={discountTypes}
                patientDefaultDiscount={patientDefaultDiscount}
                disabledByBilling={isBilledProduct}
              />
            </Col>
          )}
          <Col xs={24} sm={8}>
            <Input
              label='Price'
              required
              value={values.price}
              name='price'
              onChange={handleChange}
              placeholder='Price'
              disabled={isBilledProduct}
              prefix={
                <Icon style={{ marginLeft: 6 }} icon='si:dollar-fill' width={16} height={16} />
              }
              error={errors.price as string | undefined}
            />
          </Col>
          {isDiscountVisible && (
            <Col xs={24}>
              <DiscountHelperText patientDefaultDiscount={patientDefaultDiscount} />
            </Col>
          )}
        </Row>
        {scannerSlot}
        {scanError && (
          <div className={styles.scanErrorBanner} role='alert' aria-live='polite'>
            <Icon icon='mdi:alert-circle-outline' width={20} height={20} />
            <span className={styles.scanErrorMessage}>{scanError}</span>
            <button
              type='button'
              className={styles.scanErrorDismiss}
              onClick={dismissScanError}
              aria-label='Dismiss error'>
              <Icon icon='mdi:close' width={18} height={18} />
            </button>
          </div>
        )}
        <Row justify='space-between'>
          <Col>
            {isEditMode && !isBilledProduct && (
              <Button
                disabled={isSubmitting || isDeleting}
                severity={BUTTON_SEVERITY.DANGER}
                onClick={handleDeleteClick}
                htmlType='button'>
                Delete
              </Button>
            )}
          </Col>
          <Col>
            <div className={styles.footerActions}>
              <Button disabled={isSubmitting || isDeleting || isBilledProduct} htmlType='submit'>
                Save
              </Button>
            </div>
          </Col>
        </Row>
      </form>
    )
  },
)

ManageProductForm.displayName = 'ManageProductForm'
